export type OnboardingPath = 'learn' | 'fundamentals' | 'install';
export type BusinessStage = 'operating' | 'starting';
export type CycleIntent = 'start_now' | 'plan_next';

export interface PracticeAnswers {
  stage: BusinessStage;
  meetsWeekly: boolean;
  setsQuarterlyPriorities: boolean;
}

export type PracticeAnswersDraft = Partial<PracticeAnswers>;

export interface PersonalAnswers {
  setsPersonalPriorities: boolean;
}

export function isComplete(draft: PracticeAnswersDraft): draft is PracticeAnswers {
  return draft.stage !== undefined && draft.meetsWeekly !== undefined && draft.setsQuarterlyPriorities !== undefined;
}

export interface OnboardingDraft {
  answers: PracticeAnswersDraft | PersonalAnswers | null;
  recommendedPath: OnboardingPath | null;
  chosenPath: OnboardingPath | null;
  cycleIntent: CycleIntent | null;
  recordedAt: string | null;
}

export const DEFAULT_ONBOARDING_PATH: OnboardingPath = 'fundamentals';

export function needsSetupPath(
  settings: { setupChecklist: unknown; onboarding: OnboardingDraft },
  isPersonal: boolean,
): boolean {
  if (settings.setupChecklist !== null || settings.onboarding.recordedAt !== null) return false;
  return isPersonal ? settings.onboarding.answers === null : settings.onboarding.chosenPath === null;
}

export function asksForPriorities(answers: OnboardingDraft['answers']): boolean {
  if (!answers) return false;
  return 'setsPersonalPriorities' in answers ? answers.setsPersonalPriorities : answers.setsQuarterlyPriorities === true;
}

export const ONBOARDING_PATHS: OnboardingPath[] = ['learn', 'fundamentals', 'install'];

export const ONBOARDING_PATH_COPY: Record<OnboardingPath, { title: string; note: string }> = {
  learn: {
    title: 'Learn as you go',
    note: 'New to running on an operating system. Set up the essentials and learn each practice as you use it.',
  },
  fundamentals: {
    title: 'Set up fundamentals',
    note: 'An established business without an operating system. Put the core practices in place.',
  },
  install: {
    title: 'Install my system',
    note: 'Already running one, or starting fresh with experience. Bring the practices you know.',
  },
};

export function recommendPath(answers: PracticeAnswers): OnboardingPath {
  if (answers.meetsWeekly && answers.setsQuarterlyPriorities) return 'install';
  return answers.stage === 'operating' ? 'fundamentals' : 'learn';
}

export function isPracticeAnswers(value: unknown): value is PracticeAnswers {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (v.stage === 'operating' || v.stage === 'starting')
    && typeof v.meetsWeekly === 'boolean'
    && typeof v.setsQuarterlyPriorities === 'boolean';
}

export function isPracticeAnswersDraft(value: unknown): value is PracticeAnswersDraft {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  const keys = Object.keys(v);
  return keys.length > 0
    && keys.every((key) => key === 'stage' || key === 'meetsWeekly' || key === 'setsQuarterlyPriorities')
    && (v.stage === undefined || v.stage === 'operating' || v.stage === 'starting')
    && (v.meetsWeekly === undefined || typeof v.meetsWeekly === 'boolean')
    && (v.setsQuarterlyPriorities === undefined || typeof v.setsQuarterlyPriorities === 'boolean');
}

export function isPersonalAnswers(value: unknown): value is PersonalAnswers {
  return !!value && typeof value === 'object' && typeof (value as Record<string, unknown>).setsPersonalPriorities === 'boolean';
}

export function defaultCycleIntent(today: string): CycleIntent {
  const month = Number(today.slice(5, 7));
  return month % 3 === 0 ? 'plan_next' : 'start_now';
}
